import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { InputField, ZipCodeInputField } from "@coverme/forms";
import * as SmartyStreetsSDK from "smartystreets-javascript-sdk";
import * as sdkUtils from "smartystreets-javascript-sdk-utils";
import { connect, Field, FastField } from "formik";
import { SimpleSingleSelect } from "../FormComponents/SimpleSingleSelect";
import { add, debounce } from "lodash-es";
import { useSmartyStreets } from "../../hooks/useSmartyStreets";
import { get } from "lodash-es";

function getSuggestions(autoCompleteClient, query, selected, customerJurisdictions) {
    const lookup = new SmartyStreetsSDK.usAutocompletePro.Lookup(query);
    if (customerJurisdictions == null || customerJurisdictions.length === 0) {
        lookup.preferStates = ["MS"];
    } else {
        /*This is a hack.
        Smartystreets is only returning address from primary jurisdiction
        Since Wilbarger has texas and OK, it is alphabetized. 
        So we're always taking the last address. We need a better way
        to handle this */
        var primaryJurisdiction = customerJurisdictions.slice().reverse()[0];
        lookup.preferStates = [primaryJurisdiction];
    }

    if (selected) {
        lookup.selected = selected;
    }

    return autoCompleteClient
        .send(lookup)
        .then(response => {
            return response.result.map(suggestion => {
                var whiteSpace = "";
                let secondary = suggestion.secondary;
                if (suggestion.secondary || suggestion.entries > 1) {
                    if (suggestion.entries > 1) {
                        secondary += " (" + suggestion.entries + " more entries)";
                    }
                    whiteSpace = " ";
                }

                var address1 =
                    suggestion.streetLine + whiteSpace + secondary + " " + suggestion.city + ", " + suggestion.state;

                return {
                    ...suggestion,
                    address: address1,
                };
            });
        })
        .catch(console.warn);
}

const AutoComplete = ({
    onSelect,
    autoCompleteClient,
    errorMessage,
    validState,
    required,
    value,
    onChange,
    disabled,
    customerJurisdictions,
    ...inputProps
}) => {
    const [suggestions, setSuggestions] = useState([]);
    const [text, setText] = useState("");
    const listContainer = useRef();

    function loadSuggestionsWithoutDebounce(autoCompleteClient, value, selected, customerJurisdictions) {
        if (value === "" || value == null) {
            setSuggestions([]);
            return;
        }

        getSuggestions(autoCompleteClient, value, selected, customerJurisdictions).then(result => {
            setSuggestions(result || []);
        });
    }

    const loadSuggestions = useCallback(debounce(loadSuggestionsWithoutDebounce, 500), []);

    useEffect(() => {
        setText(value);
    }, [value]);

    function onInputChange(value) {
        setText(value);
        loadSuggestions(autoCompleteClient, value, null, customerJurisdictions);
    }

    useEffect(() => {
        const node = listContainer.current;

        function onKeyDown(e) {
            const active = document.activeElement;
            if (e.keyCode === 40 && active.nextSibling) {
                active.nextSibling.focus();
                e.preventDefault();
                return;
            }
            if (e.keyCode === 38 && active.previousSibling) {
                active.previousSibling.focus();
                e.preventDefault();
                return;
            }

            if (e.keyCode == 13 && active) {
                active.click();
            }
        }

        node.addEventListener("keydown", onKeyDown);

        return () => {
            node.removeEventListener("keydown", onKeyDown);
        };
    }, []);

    function onInputKeyDown(e) {
        if (e.keyCode == "40") {
            var node = listContainer.current;
            if (node.children.length > 0) {
                node.children[0].focus();
            }
            e.preventDefault();
        }
    }

    function onInternalSelect(selectedItem) {
        const selected = `${selectedItem.streetLine} ${selectedItem.secondary} (${selectedItem.entries}) ${selectedItem.city} ${selectedItem.state} ${selectedItem.zipcode}`;

        if (selectedItem.entries > 1) {
            loadSuggestions(autoCompleteClient, selectedItem.streetLine, selected);
        } else {
            var response = onSelect(selectedItem);

            if (response?.then) {
                response.then(() => {
                    setText(selectedItem.streetLine);
                    setSuggestions([]);
                });
            } else {
                setText(selectedItem.streetLine);
                setSuggestions([]);
            }
        }
    }

    return (
        <>
            <InputField
                {...inputProps}
                onChange={e => {
                    const value = e.currentTarget.value;
                    onInputChange(value);
                    if (onChange) {
                        onChange(e);
                    }
                }}
                value={text}
                onBlur={e => {
                    let activeElement = e?.relatedTarget;
                    if (activeElement == null) {
                        activeElement = document.activeElement;
                    }

                    if (activeElement?.className === "us-autocomplete-item") {
                        return;
                    }
                    setSuggestions([]);
                }}
                errorMessage={errorMessage}
                validState={validState}
                required={required}
                onKeyDown={onInputKeyDown}
                disabled={disabled}
            />
            <div
                className={`us-autocomplete-pro-menu ${suggestions.length > 0 ? "has-items" : ""}`}
                ref={listContainer}
            >
                {suggestions.map((x, i) => (
                    <div className="us-autocomplete-item" key={i} tabIndex={i} onClick={onInternalSelect.bind(this, x)}>
                        {x.address}
                    </div>
                ))}
            </div>
        </>
    );
};

export const AddressAutocomplete = ({
    errorMessage,
    validState,
    required,
    onSelect,
    value,
    disabled,
    onChange,
    customerJurisdictions,
}) => {
    const websiteKey = "25536363792588717";
    const { autoCompleteClient, getAddressWithCounty } = useSmartyStreets(websiteKey);

    function onSelectInternal(item) {
        return getAddressWithCounty(item).then(address => {
            onSelect(address);
        });
    }

    function onInternalChange(e) {
        onChange(e.currentTarget.value);
    }

    return (
        <>
            <AutoComplete
                id="quick-entry-address"
                placeholder={"1234 Street Name, Apt, Suite"}
                title={"Street 1"}
                errorMessage={errorMessage}
                validState={validState}
                required={required}
                onChange={onInternalChange}
                onSelect={onSelectInternal}
                value={value}
                autoCompleteClient={autoCompleteClient}
                customerJurisdictions={customerJurisdictions}
                disabled={disabled}
            />
        </>
    );
};

const AddressFields = ({
    getValidationState,
    isRequired,
    getErrors,
    states,
    customerJurisdictions,
    counties = [],
    baseFieldName = "address",
    disabled = false,
    showCounties = false,
    formik: { setFieldValue, setFieldTouched, values },
}) => {
    function buildFieldName(fieldName) {
        if (baseFieldName) {
            return `${baseFieldName}.${fieldName}`;
        } else {
            return fieldName;
        }
    }
    function onAddressSelected(lookup) {
        const county = `${lookup.state}-${lookup.county} County`;
        setFieldValue(buildFieldName("line1"), lookup.streetLine, false);
        setFieldValue(buildFieldName("line2"), lookup.secondary, false);
        setFieldValue(buildFieldName("city"), lookup.city, false);
        setFieldValue(buildFieldName("state"), lookup.state, false);
        setFieldValue(buildFieldName("county"), county, false);
        setFieldValue(buildFieldName("zipCode"), lookup.zipcode, false);
    }

    const selectedState = get(values, buildFieldName("state"));

    const countyList = useMemo(
        () =>
            counties
                .filter(x => x.state === selectedState)
                .map(x => ({
                    value: x.key,
                    label: x.name.replace(" County", ""),
                })),
        [counties, selectedState]
    );
    return (
        <div className="form-section">
            <div className="form-control m-250-input">
                <FastField
                    name={buildFieldName("line1")}
                    render={({ field }) => (
                        <AddressAutocomplete
                            {...field}
                            id="quick-entry-address-1"
                            placeholder={"1234 Street Name, Apt, Suite"}
                            title={"Street 1"}
                            errorMessage={getErrors(field)}
                            validState={getValidationState(field)}
                            required={isRequired(field)}
                            onSelect={onAddressSelected}
                            value={field.value}
                            customerJurisdictions={customerJurisdictions}
                            disabled={disabled}
                            onChange={val => {
                                setFieldValue(field.name, val);
                            }}
                        />
                    )}
                />
            </div>
            <div className="form-control m-250-input">
                <Field
                    name={buildFieldName("line2")}
                    render={({ field }) => (
                        <InputField
                            {...field}
                            id="quick-entry-address-2"
                            placeholder={"5678 Street Name, Apt, Suite"}
                            title={"Street 2"}
                            errorMessage={getErrors(field)}
                            validState={getValidationState(field)}
                            required={isRequired(field)}
                            disabled={disabled}
                        />
                    )}
                />
            </div>
            <div className="form-control m-input">
                <FastField
                    name={buildFieldName("city")}
                    render={({ field }) => (
                        <InputField
                            {...field}
                            id="quick-entry-city"
                            placeholder={"City"}
                            title={"City"}
                            errorMessage={getErrors(field)}
                            validState={getValidationState(field)}
                            required={isRequired(field)}
                            disabled={disabled}
                        />
                    )}
                />
            </div>
            <div className="form-control s-input">
                <Field
                    name={buildFieldName("state")}
                    render={({ field }) => (
                        <SimpleSingleSelect
                            {...field}
                            title={"State"}
                            id="quick-entry-state"
                            setFieldTouched={setFieldTouched}
                            setFieldValue={setFieldValue}
                            errorMessage={getErrors(field)}
                            validState={getValidationState(field)}
                            options={states}
                            required={isRequired(field)}
                            disabled={disabled}
                        />
                    )}
                />
            </div>
            {showCounties && (
                <div className="form-control m-input">
                    <Field
                        name={buildFieldName("county")}
                        render={({ field }) => (
                            <SimpleSingleSelect
                                {...field}
                                title="County"
                                id={field.name}
                                setFieldTouched={setFieldTouched}
                                setFieldValue={setFieldValue}
                                errorMessage={getErrors(field)}
                                validState={getValidationState(field)}
                                required={isRequired(field)}
                                options={countyList}
                                disabled={disabled}
                            />
                        )}
                    />
                </div>
            )}
            <div className="form-control s-input">
                <FastField
                    name={buildFieldName("zipCode")}
                    render={({ field }) => (
                        <ZipCodeInputField
                            {...field}
                            id="quick-entry-zip"
                            changeEvent={e => setFieldValue(field.name, e)}
                            placeholder={"Zip Code"}
                            title={"Zip Code"}
                            errorMessage={getErrors(field)}
                            validState={getValidationState(field)}
                            required={isRequired(field)}
                            disabled={disabled}
                        />
                    )}
                />
            </div>
        </div>
    );
};

export default connect(AddressFields);
